import { useEffect, useRef, useState } from "react";
import Shell from "@/components/Shell";
import api, { API, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Camera, Upload, Download, RefreshCw, Sparkles, CheckCircle2, FlipHorizontal2 } from "lucide-react";

export default function MRGenerate() {
  const [languages, setLanguages] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState({
    doctor_name: "",
    qualification: "",
    hospital: "",
    mobile: "",
    message: "",
    language_code: "en",
    template_id: "",
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [generated, setGenerated] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [fileDeleted, setFileDeleted] = useState(false);

  const [camOpen, setCamOpen] = useState(false);
  const [facingMode, setFacingMode] = useState("user");
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    Promise.all([api.get("/languages"), api.get("/templates")]).then(([l, t]) => {
      setLanguages(l.data);
      setTemplates(t.data);
      const enTpl = t.data.find((x) => x.language_code === "en") || t.data[0];
      const enLang = l.data.find((x) => x.code === "en") || l.data[0];
      if (enTpl) setForm((f) => ({ ...f, template_id: enTpl.id, language_code: enTpl.language_code, message: enLang?.default_message || "" }));
    });
  }, []);

  const onLanguageChange = (code) => {
    const tpl = templates.find((t) => t.language_code === code);
    const lang = languages.find((l) => l.code === code);
    setForm((f) => ({
      ...f,
      language_code: code,
      template_id: tpl?.id || f.template_id,
      message: f.message && f.message !== (languages.find((l) => l.code === f.language_code)?.default_message || "")
        ? f.message
        : (lang?.default_message || ""),
    }));
  };

  const onFile = (file) => {
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/jpg", "image/webp"].includes(file.type)) {
      toast.error("Please pick a PNG, JPG or WEBP image");
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error("Image too large (max 20MB)");
      return;
    }
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setPhotoPreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const startCamera = async (mode = "user") => {
    // Stop any existing stream before starting a new one
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    try {
      setCamOpen(true);
      setFacingMode(mode);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: mode, width: { ideal: 1280 } },
        audio: false,
      });
      streamRef.current = stream;
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
      }, 50);
    } catch {
      toast.error("Cannot access camera. Please allow camera permission.");
      setCamOpen(false);
    }
  };

  const switchCamera = () => {
    const next = facingMode === "user" ? "environment" : "user";
    startCamera(next);
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setCamOpen(false);
  };

  const captureCamera = () => {
    const v = videoRef.current;
    if (!v) return;
    const c = document.createElement("canvas");
    c.width = v.videoWidth;
    c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0);
    c.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], `capture-${Date.now()}.jpg`, { type: "image/jpeg" });
      onFile(file);
      stopCamera();
      toast.success("Photo captured");
    }, "image/jpeg", 0.92);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!photoFile) { toast.error("Please upload or capture a doctor photo"); return; }
    if (!form.template_id) { toast.error("Please select a template/language"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ""));
      fd.append("photo", photoFile);
      const { data } = await api.post("/posters/generate", fd);
      setGenerated(data);
      toast.success("Poster generated!");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setGenerated(null);
    setPhotoFile(null);
    setPhotoPreview(null);
    setFileDeleted(false);
    setDownloading(null);
  };

  const handleDownload = async (fmt) => {
    if (fileDeleted || downloading) return;
    setDownloading(fmt);
    try {
      const res = await fetch(`${API}/posters/${generated.id}/download?fmt=${fmt}`, { credentials: "include" });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Download failed");
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${generated.doctor_name.replace(/ /g, "_")}_WHD.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
      setFileDeleted(true);
      toast.success("Download successful");
    } catch {
      toast.error("Download failed");
    } finally {
      setDownloading(null);
    }
  };

  return (
    <Shell role="mr">
      <div className="mb-6">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">Poster Generator</div>
        <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900">Create a personalized poster</h1>
        <p className="text-slate-500 mt-1">Fill the details, add the doctor's photo, generate, download, and share.</p>
      </div>

      <div className="grid lg:grid-cols-[1fr_1fr] gap-6 lg:gap-10">
        {/* Form */}
        <form onSubmit={onSubmit} className="space-y-5" data-testid="generate-form">
          <Card className="p-5 md:p-6 space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Doctor Name *">
                <Input
                  data-testid="input-doctor-name"
                  value={form.doctor_name}
                  onChange={(e) => setForm({ ...form, doctor_name: e.target.value })}
                  placeholder="Dr. Rajesh Shirvastav" required
                />
              </Field>
              <Field label="Qualification *">
                <Input
                  data-testid="input-qualification"
                  value={form.qualification}
                  onChange={(e) => setForm({ ...form, qualification: e.target.value })}
                  placeholder="M.B.B.S., MD." required
                />
              </Field>
            </div>
            <Field label="Hospital / Clinic *">
              <Input
                data-testid="input-hospital"
                value={form.hospital}
                onChange={(e) => setForm({ ...form, hospital: e.target.value })}
                placeholder="Swastik Hospital, Pune" required
              />
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Mobile (optional)">
                <Input
                  data-testid="input-mobile"
                  value={form.mobile}
                  onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                  placeholder="+91 ..."
                />
              </Field>
              <Field label="Language *">
                <Select value={form.language_code} onValueChange={onLanguageChange}>
                  <SelectTrigger data-testid="select-language" className="h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {languages.map((l) => (
                      <SelectItem key={l.id} value={l.code} data-testid={`lang-${l.code}`}>{l.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <Field label="Personalized Message (optional)">
              <Textarea
                data-testid="input-message"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Leave blank to use default message…"
                className="min-h-[110px]"
              />
              <div className="text-xs text-slate-400 mt-1">If left empty, the language's default message is used.</div>
            </Field>
          </Card>

          {/* Photo upload */}
          <Card className="p-5 md:p-6">
            <div className="mb-4">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Doctor Photo</div>
              <div className="font-display font-bold text-slate-900 mt-1">Upload or capture</div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 items-stretch">
              <div className="flex-1 grid grid-cols-2 gap-3">
                <Button type="button" variant="outline" className="h-24 flex flex-col gap-2" onClick={() => fileInputRef.current?.click()} data-testid="btn-upload-photo">
                  <Upload className="w-5 h-5" />
                  <span className="text-sm font-semibold">Upload</span>
                </Button>
                <Button type="button" variant="outline" className="h-24 flex flex-col gap-2" onClick={startCamera} data-testid="btn-capture-photo">
                  <Camera className="w-5 h-5" />
                  <span className="text-sm font-semibold">Camera</span>
                </Button>
                <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/jpg,image/webp" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} data-testid="file-input" />
              </div>
              <div className="w-full sm:w-40 h-40 rounded-lg border border-dashed border-slate-300 bg-slate-50 grid place-items-center overflow-hidden" data-testid="photo-preview">
                {photoPreview ? (
                  <img src={photoPreview} alt="Doctor" className="w-full h-full object-cover" />
                ) : (
                  <div className="text-xs text-slate-400 text-center px-2">No photo<br/>selected</div>
                )}
              </div>
            </div>
          </Card>

          <div className="flex gap-3">
            <Button type="submit" disabled={busy} className="h-12 flex-1 bg-brand-600 hover:bg-brand-700 gap-2" data-testid="btn-generate">
              {busy ? <><RefreshCw className="w-4 h-4 animate-spin" /> Generating…</> : <><Sparkles className="w-4 h-4" /> Generate Poster</>}
            </Button>
            {generated && (
              <Button type="button" variant="outline" className="h-12" onClick={reset} data-testid="btn-reset">
                Start New
              </Button>
            )}
          </div>
        </form>

        {/* Preview */}
        <div className="lg:sticky lg:top-6 self-start">
          <Card className="p-3 canvas-checker">
            <div className="bg-white rounded-md p-2">
              {generated ? (
                <img
                  src={`${API}/posters/${generated.id}/preview?t=${Date.now()}`}
                  alt="Generated poster"
                  className="w-full rounded"
                  data-testid="generated-poster-img"
                />
              ) : (
                <img
                  src={`${API}/templates/${form.template_id}/file?t=${form.template_id}`}
                  alt="Template preview"
                  className="w-full rounded opacity-90"
                  data-testid="template-preview-img"
                  onError={(e) => { e.target.style.opacity = 0.2; }}
                />
              )}
            </div>
          </Card>
          {generated && (
            fileDeleted ? (
              <div className="mt-4 flex items-center gap-3 p-4 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700">
                <CheckCircle2 className="w-5 h-5 shrink-0" />
                <div className="text-sm font-semibold">Download successful</div>
              </div>
            ) : (
              <div className="mt-4">
                <Button
                  className="w-full h-12 bg-brand-600 hover:bg-brand-700 gap-2"
                  onClick={() => handleDownload("jpg")}
                  disabled={!!downloading}
                  data-testid="btn-download-poster"
                >
                  {downloading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  Download Poster
                </Button>
              </div>
            )
          )}
        </div>
      </div>

      {/* Camera dialog */}
      <Dialog open={camOpen} onOpenChange={(o) => (o ? null : stopCamera())}>
        <DialogContent className="max-w-md" data-testid="camera-dialog">
          <DialogHeader>
            <DialogTitle>Capture Doctor Photo</DialogTitle>
          </DialogHeader>
          <div className="aspect-[3/4] bg-black rounded-md overflow-hidden relative">
            <video ref={videoRef} className="w-full h-full object-cover" playsInline muted />
            {/* Switch camera button — top-right corner */}
            <button
              type="button"
              onClick={switchCamera}
              className="absolute top-3 right-3 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 text-white grid place-items-center transition"
              title={facingMode === "user" ? "Switch to back camera" : "Switch to front camera"}
              data-testid="btn-cam-switch"
            >
              <FlipHorizontal2 className="w-5 h-5" />
            </button>
          </div>
          <div className="flex gap-3">
            <Button onClick={captureCamera} className="flex-1 bg-brand-600 hover:bg-brand-700" data-testid="btn-cam-capture">
              <Camera className="w-4 h-4 mr-2"/>Capture
            </Button>
            <Button variant="outline" onClick={stopCamera} data-testid="btn-cam-cancel">Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">{label}</Label>
    {children}
  </div>
);
